/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {BaseFields, BaseModel, BaseProps, createFieldDef, FieldDefinition, ModelFieldType} from './baseModel';

import {ExportInvoiceFilter, ExportNameAttr, ExportProcessStatus, InvoiceExportType} from '../util/interfaces';

import {downloadRequiredFile, IndexedObject, ModelResponse, nullify, ResponseData} from '@dvrd/dvr-controls';

import IDate from '@dvrd/idate';

import Account from './accountModel';

import {downloadExportProcess, exportInvoices, getExportInvoiceCount, getExportProcesses} from '../util/requestSender';

import {deepClone} from '../util/utils';

interface Props extends BaseProps {
    status?: ExportProcessStatus;
    export_type?: InvoiceExportType;
    from_date?: string;
    to_date?: string;
    file_name?: string;
    flex?: IndexedObject<any>;
    account_id?: string;
    account?: Account | any;
}

export interface ExportProcessFields extends BaseFields {
    status: ExportProcessStatus;
    export_type: InvoiceExportType;
    from_date: string;
    to_date: string;
    file_name: string;
    flex: IndexedObject<any>;
    account_id: string;
    account: Account | null;
}

const fields: ExportProcessFields = {
    id: '',
    created_at: '',
    updated_at: '',
    status: ExportProcessStatus.EXPORTING,
    export_type: InvoiceExportType.CSV,
    from_date: '',
    to_date: '',
    file_name: '',
    flex: {},
    account_id: '',
    account: null
};

const fieldsDefinition: {[key in keyof Omit<ExportProcessFields, 'account'>]: FieldDefinition} = {
    id: createFieldDef(ModelFieldType.STRING),
    created_at: createFieldDef(ModelFieldType.STRING),
    updated_at: createFieldDef(ModelFieldType.STRING, undefined, true),
    from_date: createFieldDef(ModelFieldType.STRING),
    to_date: createFieldDef(ModelFieldType.STRING),
    account_id: createFieldDef(ModelFieldType.STRING),
    export_type: createFieldDef(ModelFieldType.STRING, InvoiceExportType.CSV),
    status: createFieldDef(ModelFieldType.STRING, ExportProcessStatus.EXPORTING),
    file_name: createFieldDef(ModelFieldType.STRING),
    flex: createFieldDef(ModelFieldType.OBJECT)
};

const ignoreProps: Array<keyof Props> = ['account'];

type ListFilter = {
    account_id: string;
    export_types?: Array<InvoiceExportType>;
    period_start?: string;
    period_end?: string;
};

export default class ExportProcess extends BaseModel<Props, ExportProcessFields> {
    static get = (_id: string, _callback: ModelResponse<ExportProcess>) => {};

    static getAll = (data: ListFilter, callback: ModelResponse<ExportProcess[]>) => {
        getExportProcesses({
            data,
            callback: (data: ResponseData) => {
                const processes: ExportProcess[] = data.success
                    ? data.processes.map((proc: any) => new ExportProcess(proc))
                    : [];
                callback(processes, data.success, data);
            }
        });
    };

    constructor(props?: Props) {
        super(ignoreProps, fieldsDefinition, fields, props);
        this.build(props);
    }

    get id(): string {
        return this.fields.id as string;
    }

    get status(): ExportProcessStatus {
        return this.fields.status;
    }

    set status(status: ExportProcessStatus) {
        this.fields.status = status;
    }

    get exportType(): InvoiceExportType {
        return this.fields.export_type;
    }

    get translatedExportType(): string {
        const {exportType} = this;
        if (exportType === InvoiceExportType.COMBINED_PDF) return 'Gecombineerde PDF';
        else if (exportType === InvoiceExportType.VAT_DECL_CSV) return 'CSV';
        else if (exportType === InvoiceExportType.VAT_DECL_EXCEL) return 'Excel';
        else if (exportType === InvoiceExportType.CSV_CUSTOM) return 'CSV (aangepast)';
        return exportType.toUpperCase();
    }

    set exportType(export_type: InvoiceExportType) {
        this.fields.export_type = export_type;
    }

    get fromDate(): string {
        return this.fields.from_date;
    }

    get fromDateMoment(): IDate | null {
        if (!this.fromDate) return null;
        return new IDate(this.fromDate);
    }

    set fromDate(from_date: string) {
        this.fields.from_date = from_date;
    }

    get toDate(): string {
        return this.fields.to_date;
    }

    get toDateMoment(): IDate | null {
        if (!this.toDate) return null;
        return new IDate(this.toDate);
    }

    set toDate(to_date: string) {
        this.fields.to_date = to_date;
    }

    get filename(): string {
        return this.fields.file_name;
    }

    set filename(filename: string) {
        this.fields.file_name = filename;
    }

    get accountId(): string {
        return this.fields.account_id;
    }

    set accountId(account_id: string) {
        this.fields.account_id = account_id;
    }

    get account(): Account | null {
        return this.fields.account;
    }

    set account(account: Account | null) {
        this.fields.account = account;
        if (account) this.fields.account_id = account.id;
    }

    get periodLabel(): string {
        return `${this.fromDateMoment!.format('DD/MM/YYYY')} - ${this.toDateMoment!.format('DD/MM/YYYY')}`;
    }

    get flex(): IndexedObject<any> {
        return this.fields.flex;
    }

    clone = (deep: boolean = true): ExportProcess => {
        let fields = this.fields;
        if (deep) fields = deepClone(fields);
        return new ExportProcess(fields);
    };

    getFlex = (key: string): any | null => {
        return this.fields.flex[key] ?? null;
    };

    getCount = (callback: ModelResponse<ExportProcess>, filter?: ExportInvoiceFilter) => {
        getExportInvoiceCount({
            data: {
                account_id: this.accountId,
                from_date: this.fromDate,
                to_date: this.toDate,
                country: nullify(filter?.country),
                reverse_charge_only: filter?.reverseChargeOnly,
                customer_type: filter?.customerType,
                fulfilment: nullify(filter?.fulfilment)
            },
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    export = (callback: ModelResponse<ExportProcess>, filter?: ExportInvoiceFilter, name_attr?: ExportNameAttr) => {
        exportInvoices({
            data: {
                to_date: this.toDate,
                from_date: this.fromDate,
                account_id: this.accountId,
                export_type: this.exportType,
                country: nullify(filter?.country),
                fulfilment: nullify(filter?.fulfilment),
                reverse_charge_only: filter?.reverseChargeOnly,
                customer_type: filter?.customerType,
                name_attr,
                order_field_names: filter?.order_field_names,
                invoice_field_names: filter?.invoice_field_names
            },
            callback: (data: ResponseData) => {
                callback(this, data.success, data);
            }
        });
    };

    download = (onFinished: VoidFunction, onError: (data: ResponseData) => void) => {
        downloadExportProcess({
            data: {account_id: this.accountId, export_process_id: this.id},
            callback: (data: ResponseData) => {
                if (data.success) {
                    downloadRequiredFile(data.url);
                    onFinished();
                } else onError(data);
            }
        });
    };

    protected build = (props?: Props) => {
        this.constructFields(props);
        if (props) {
            if (props.account) {
                this.account = props.account instanceof Account ? props.account : new Account(props.account);
            }
        }
    };
}
